// src/components/descuentos/DescuentoList.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Search, Calendar, Percent, DollarSign, Edit, Eye, EyeOff, Package } from 'lucide-react';
import { DescuentoService } from '../../services/descuentoService';
import { ProductoService } from '../../services/productoService';
import theme, { cn } from '../../styles/theme';

const DescuentoList = ({ sucursalId }) => {
  const [view, setView] = useState('list');
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [descuentos, setDescuentos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    valor: '',
    fecha_inicio: '',
    fecha_fin: '',
    estado: 'ACTIVO',
    id_sucursal: sucursalId,
    limite_usos: false,
    cantidad_usos: '',
    productoIds: []
  });

  // ✅ Convierte fecha a string local YYYY-MM-DD sin restar horas UTC
  const toLocalDate = (f) => {
    if (!f) return '';
    const d = new Date(f);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // ✅ Formatea fecha para mostrar en pantalla usando métodos locales
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    console.log('🔍 DescuentoList montado con sucursalId:', sucursalId);
    loadDescuentos();
  }, [sucursalId]);

  useEffect(() => {
    if (view === 'form') loadProductos();
  }, [view, sucursalId]);

  const loadDescuentos = async () => {
    try {
      setLoading(true);
      const descuentosData = await DescuentoService.getBySucursal(sucursalId);
      setDescuentos(descuentosData || []);
    } catch (error) {
      console.error('❌ Error al cargar descuentos:', error);
      alert('Error al cargar los descuentos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadProductos = async () => {
    try {
      setLoadingProductos(true);
      const productosData = await ProductoService.getAllPr(sucursalId, false);
      setProductos(productosData || []);
    } catch (error) {
      console.error('❌ Error al cargar productos:', error);
      alert('Error al cargar productos: ' + error.message);
      setProductos([]);
    } finally {
      setLoadingProductos(false);
    }
  };

  const handleCreateNew = () => {
    setEditingDiscount(null);
    setFormData({
      nombre: '', descripcion: '', valor: '', fecha_inicio: '', fecha_fin: '',
      estado: 'ACTIVO', id_sucursal: sucursalId, limite_usos: false, cantidad_usos: '', productoIds: []
    });
    setView('form');
  };

  const handleEdit = (descuento) => {
    const idDescuento = descuento.id_descuento || descuento.Id_descuento;
    if (!idDescuento) { alert('❌ Error: No se encontró el ID del descuento'); return; }

    setEditingDiscount(descuento);

    const productos = descuento.productos || descuento.Productos || [];
    const productIds = productos.map(p => p.id_producto || p.Id_producto).filter(Boolean);

    setFormData({
      nombre:        descuento.nombre        || descuento.Nombre        || '',
      descripcion:   descuento.descripcion   || descuento.Descripcion   || '',
      valor:         descuento.valor         ?? descuento.Valor         ?? '',
      fecha_inicio:  toLocalDate(descuento.fecha_inicio || descuento.Fecha_inicio), // ✅
      fecha_fin:     toLocalDate(descuento.fecha_fin    || descuento.Fecha_fin),    // ✅
      estado:        descuento.estado        || descuento.Estado        || 'ACTIVO',
      id_sucursal:   sucursalId,
      limite_usos:   descuento.limite_usos   ?? descuento.Limite_usos   ?? false,
      cantidad_usos: descuento.cantidad_usos ?? descuento.Cantidad_usos ?? '',
      productoIds:   productIds
    });
    setView('form');
  };

  const handleToggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      productoIds: prev.productoIds.includes(productId)
        ? prev.productoIds.filter(id => id !== productId)
        : [...prev.productoIds, productId]
    }));
  };

  const handleSave = async () => {
    try {
      if (!formData.nombre.trim())                                              { alert('⚠️ El nombre del descuento es requerido'); return; }
      if (!formData.valor || formData.valor <= 0)                               { alert('⚠️ El valor del descuento debe ser mayor a 0'); return; }
      if (!formData.fecha_inicio || !formData.fecha_fin)                        { alert('⚠️ Las fechas de inicio y fin son requeridas'); return; }
      if (new Date(formData.fecha_fin) <= new Date(formData.fecha_inicio))      { alert('⚠️ La fecha de fin debe ser posterior a la fecha de inicio'); return; }
      if (formData.productoIds.length === 0)                                    { alert('⚠️ Debe seleccionar al menos un producto'); return; }
      if (formData.limite_usos && (!formData.cantidad_usos || formData.cantidad_usos <= 0)) { alert('⚠️ Si activa límite de usos, debe especificar una cantidad válida'); return; }

      setLoading(true);

      const dataToSend = {
        nombre:        formData.nombre.trim(),
        descripcion:   formData.descripcion?.trim() || null,
        valor:         parseFloat(formData.valor),
        fecha_inicio:  formData.fecha_inicio, // el service agrega T12:00:00
        fecha_fin:     formData.fecha_fin,
        estado:        formData.estado,
        id_sucursal:   sucursalId,
        limite_usos:   formData.limite_usos,
        cantidad_usos: formData.limite_usos ? parseInt(formData.cantidad_usos) : null,
        productoIds:   formData.productoIds
      };

      let result;
      if (editingDiscount) {
        const idDescuento = editingDiscount.id_descuento || editingDiscount.Id_descuento;
        if (!idDescuento) { alert('❌ Error: No se encontró el ID del descuento para actualizar'); return; }
        result = await DescuentoService.update(idDescuento, dataToSend);
      } else {
        result = await DescuentoService.create(dataToSend);
      }

      if (result.success) {
        alert(result.message || (editingDiscount ? '✅ Descuento actualizado correctamente' : '✅ Descuento creado correctamente'));
        await loadDescuentos();
        setView('list');
        setEditingDiscount(null);
      } else {
        alert('❌ Error: ' + (result.message || 'No se pudo guardar el descuento'));
      }
    } catch (error) {
      console.error('❌ Error al guardar:', error);
      alert('❌ Error al guardar el descuento: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este descuento?')) return;
    try {
      setLoading(true);
      const result = await DescuentoService.delete(id);
      if (result.success) { alert(result.message || '✅ Descuento eliminado correctamente'); await loadDescuentos(); }
    } catch (error) {
      console.error('Error al eliminar:', error);
      alert('❌ Error al eliminar el descuento');
    } finally { setLoading(false); }
  };

  const toggleActive = async (descuento) => {
    try {
      setLoading(true);
      const idDescuento = descuento.id_descuento || descuento.Id_descuento;
      if (!idDescuento) { alert('❌ Error: No se encontró el ID del descuento'); return; }

      const estadoActual = descuento.estado || descuento.Estado || 'INACTIVO';
      const nuevoEstado  = estadoActual === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO';

      const productos   = descuento.productos || descuento.Productos || [];
      const productIds  = productos.map(p => p.id_producto || p.Id_producto).filter(Boolean);

      const dataToUpdate = {
        nombre:        descuento.nombre        || descuento.Nombre        || '',
        descripcion:   descuento.descripcion   || descuento.Descripcion   || '',
        valor:         Number(descuento.valor  ?? descuento.Valor         ?? 0),
        fecha_inicio:  toLocalDate(descuento.fecha_inicio || descuento.Fecha_inicio), // ✅
        fecha_fin:     toLocalDate(descuento.fecha_fin    || descuento.Fecha_fin),    // ✅
        estado:        nuevoEstado,
        id_sucursal:   sucursalId,
        limite_usos:   descuento.limite_usos   ?? descuento.Limite_usos   ?? false,
        cantidad_usos: descuento.cantidad_usos ?? descuento.Cantidad_usos ?? null,
        productoIds:   productIds
      };

      const result = await DescuentoService.update(idDescuento, dataToUpdate);
      if (result.success) await loadDescuentos();
    } catch (error) {
      console.error('❌ Error al cambiar estado:', error);
      alert('❌ Error al cambiar el estado: ' + error.message);
    } finally { setLoading(false); }
  };

  const filteredProducts = productos.filter(p =>
    p.Nombre_producto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.nombre_producto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.Nombre_categoria?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.nombre_categoria?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getProductName     = (p) => p.Nombre_producto || p.nombre_producto || 'Sin nombre';
  const getProductPrice    = (p) => p.Precio || p.precio || 0;
  const getProductCategory = (p) => p.Nombre_categoria || p.nombre_categoria || 'Sin categoría';
  const getProductId       = (p) => p.Id_producto || p.id_producto;

  // ==================== VISTA DE LISTA ====================
  if (view === 'list') {
    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gestión de Descuentos</h2>
            <p className="text-sm text-gray-500 mt-1">Administra los descuentos de productos</p>
          </div>
          <button onClick={handleCreateNew} disabled={loading}
            className={cn("flex items-center gap-2 px-6 py-3 rounded-lg font-semibold", theme.classes.button.primary, theme.transitions.default, loading && "opacity-50 cursor-not-allowed")}>
            <Plus size={20} /> Nuevo Descuento
          </button>
        </div>

        {loading && descuentos.length === 0 ? (
          <div className={cn(theme.classes.card.base, "p-12 text-center")}>
            <div className="flex flex-col items-center gap-4">
              <svg className="w-12 h-12 text-gray-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <p className="text-gray-500">Cargando descuentos...</p>
            </div>
          </div>
        ) : descuentos.length === 0 ? (
          <div className={cn(theme.classes.card.base, "p-12 text-center")}>
            <div className="flex flex-col items-center gap-4">
              <Percent className="w-16 h-16 text-gray-300" />
              <div>
                <p className="text-lg font-semibold text-gray-700">No hay descuentos creados</p>
                <p className="text-sm text-gray-500 mt-1">Crea tu primer descuento haciendo clic en el botón de arriba</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            {descuentos.map(descuento => {
              const idDescuento  = descuento.id_descuento  || descuento.Id_descuento;
              const nombre       = descuento.nombre        || descuento.Nombre        || 'Sin nombre';
              const descripcion  = descuento.descripcion   || descuento.Descripcion;
              const valor        = Number(descuento.valor  ?? descuento.Valor         ?? 0);
              const estado       = descuento.estado        || descuento.Estado        || 'INACTIVO';
              const fechaInicio  = descuento.fecha_inicio  || descuento.Fecha_inicio;
              const fechaFin     = descuento.fecha_fin     || descuento.Fecha_fin;
              const limiteUsos   = descuento.limite_usos   ?? descuento.Limite_usos   ?? false;
              const cantidadUsos = descuento.cantidad_usos ?? descuento.Cantidad_usos ?? 0;
              const usosActuales = descuento.usos_actuales ?? descuento.Usos_actuales ?? 0;
              const productos    = descuento.productos     || descuento.Productos     || [];

              return (
                <div key={idDescuento || Math.random()} className={cn(theme.classes.card.base, theme.classes.card.hover, "p-6")}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-800">{nombre}</h3>
                        <span className={cn("px-3 py-1 rounded-full text-sm font-semibold",
                          estado === 'ACTIVO' ? theme.classes.status.active : theme.classes.status.inactive)}>
                          {estado}
                        </span>
                      </div>
                      {descripcion && <p className="text-sm text-gray-600 mb-3">{descripcion}</p>}
                      <div className="flex items-center gap-4 text-gray-600 text-sm mb-3">
                        <div className="flex items-center gap-1">
                          <DollarSign size={16} />
                          <span className="font-semibold text-lg text-blue-600">S/ {valor.toFixed(2)}</span>
                        </div>
                        {fechaInicio && fechaFin && (
                          <div className="flex items-center gap-1">
                            <Calendar size={16} />
                            <span>{formatDate(fechaInicio)} al {formatDate(fechaFin)}</span>
                          </div>
                        )}
                      </div>
                      {limiteUsos && cantidadUsos > 0 && (
                        <div className="mb-3">
                          <p className="text-sm text-gray-600 mb-1">Usos: {usosActuales} / {cantidadUsos}</p>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full transition-all"
                              style={{ width: `${Math.min((usosActuales / cantidadUsos) * 100, 100)}%` }} />
                          </div>
                        </div>
                      )}
                      <div className="border-t pt-3 mt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Package size={16} className="text-gray-500" />
                          <p className="text-sm font-semibold text-gray-700">Productos con descuento ({productos.length}):</p>
                        </div>
                        {productos.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {productos.slice(0, 5).map((producto, idx) => {
                              const idProducto        = producto.id_producto || producto.Id_producto || idx;
                              const nombreProducto    = producto.nombre_producto || producto.Nombre_producto || 'Sin nombre';
                              const precioProducto    = Number(producto.precio ?? producto.Precio ?? 0);
                              const precioConDescuento = Math.max(0, precioProducto - valor);
                              return (
                                <div key={idProducto} className="bg-blue-50 px-3 py-1 rounded-full text-sm flex items-center gap-2 border border-blue-200">
                                  <span className="text-gray-700">{nombreProducto}</span>
                                  <span className="text-gray-500">S/ {precioProducto.toFixed(2)} → S/ {precioConDescuento.toFixed(2)}</span>
                                </div>
                              );
                            })}
                            {productos.length > 5 && (
                              <span className="px-3 py-1 bg-gray-200 text-gray-600 text-sm rounded-full font-medium">+{productos.length - 5} más</span>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No hay productos asociados</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button onClick={() => toggleActive(descuento)} disabled={loading}
                        className={cn("p-2 rounded transition-colors", loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100", estado === 'ACTIVO' ? "text-green-600" : "text-gray-600")}
                        title={estado === 'ACTIVO' ? 'Desactivar' : 'Activar'}>
                        {estado === 'ACTIVO' ? <Eye size={20} /> : <EyeOff size={20} />}
                      </button>
                      <button onClick={() => handleEdit(descuento)} disabled={loading}
                        className={cn("p-2 text-blue-600 rounded transition-colors", loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-50")}
                        title="Editar"><Edit size={20} /></button>
                      <button onClick={() => handleDelete(idDescuento)} disabled={loading || !idDescuento}
                        className={cn("p-2 text-red-600 rounded transition-colors", (loading || !idDescuento) ? "opacity-50 cursor-not-allowed" : "hover:bg-red-50")}
                        title="Eliminar"><Trash2 size={20} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ==================== VISTA DE FORMULARIO ====================
  return (
    <div className="space-y-6">
      <div className="mb-6">
        <button onClick={() => setView('list')} className="text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-2">
          ← Volver a la lista
        </button>
        <h1 className="text-3xl font-bold text-gray-800">
          {editingDiscount ? 'Editar Descuento' : 'Crear Nuevo Descuento'}
        </h1>
      </div>

      <div className={cn(theme.classes.card.base, "p-6")}>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className={cn(theme.classes.form.label, "mb-2")}>Nombre del Descuento *</label>
            <input type="text" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              placeholder="Ej: Promo Pizzas del Mes" className={theme.classes.form.input} />
          </div>
          <div>
            <label className={cn(theme.classes.form.label, "mb-2")}>Descripción (opcional)</label>
            <input type="text" value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Descripción del descuento" className={theme.classes.form.input} />
          </div>
          <div>
            <label className={cn(theme.classes.form.label, "mb-2")}>Valor del Descuento (Monto Fijo) *</label>
            <div className="relative">
              <input type="number" value={formData.valor} onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                placeholder="5.00" step="0.01" className={theme.classes.form.input} />
              <span className="absolute right-3 top-3 text-gray-500">S/</span>
            </div>
          </div>
          <div>
            <label className={cn(theme.classes.form.label, "mb-2")}>Fecha de Inicio *</label>
            <input type="date" value={formData.fecha_inicio} onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
              className={theme.classes.form.input} />
          </div>
          <div>
            <label className={cn(theme.classes.form.label, "mb-2")}>Fecha de Fin *</label>
            <input type="date" value={formData.fecha_fin} onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
              className={theme.classes.form.input} />
          </div>
          <div>
            <label className={cn(theme.classes.form.label, "mb-2")}>Estado</label>
            <select value={formData.estado} onChange={(e) => setFormData({ ...formData, estado: e.target.value })} className={theme.classes.form.select}>
              <option value="ACTIVO">Activo</option>
              <option value="INACTIVO">Inactivo</option>
            </select>
          </div>
          <div>
            <label className={cn(theme.classes.form.label, "mb-2 flex items-center gap-2")}>
              <input type="checkbox" checked={formData.limite_usos}
                onChange={(e) => setFormData({ ...formData, limite_usos: e.target.checked, cantidad_usos: e.target.checked ? formData.cantidad_usos : '' })}
                className="w-4 h-4 text-blue-600 rounded" />
              Límite de Usos
            </label>
            {formData.limite_usos && (
              <input type="number" value={formData.cantidad_usos} onChange={(e) => setFormData({ ...formData, cantidad_usos: e.target.value })}
                placeholder="Ej: 100" className={cn(theme.classes.form.input, "mt-2")} min="1" />
            )}
          </div>
        </div>

        <div className="border-t pt-6">
          <div className="mb-4">
            <label className={cn(theme.classes.form.label, "mb-2")}>
              Seleccionar Productos * ({formData.productoIds.length} seleccionados)
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar productos..." className={cn(theme.classes.form.input, "pl-10")} />
            </div>
          </div>

          {loadingProductos ? (
            <div className="flex items-center justify-center p-12 border rounded-lg bg-gray-50">
              <div className="flex flex-col items-center gap-4">
                <svg className="w-12 h-12 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <p className="text-gray-600 font-medium">Cargando productos...</p>
              </div>
            </div>
          ) : productos.length === 0 ? (
            <div className="flex items-center justify-center p-12 border rounded-lg bg-gray-50">
              <div className="flex flex-col items-center gap-4">
                <Package className="w-16 h-16 text-gray-300" />
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-700">No hay productos disponibles</p>
                  <p className="text-sm text-gray-500 mt-1">Verifica que existan productos activos en esta sucursal</p>
                </div>
              </div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="flex items-center justify-center p-12 border rounded-lg bg-gray-50">
              <div className="flex flex-col items-center gap-4">
                <Search className="w-16 h-16 text-gray-300" />
                <p className="text-gray-600">No se encontraron productos con "{searchTerm}"</p>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3 max-h-96 overflow-y-auto border rounded-lg p-4 bg-gray-50">
              {filteredProducts.map(product => {
                const productId       = getProductId(product);
                const isSelected      = formData.productoIds.includes(productId);
                const productPrice    = getProductPrice(product);
                const discountedPrice = formData.valor ? Math.max(0, productPrice - parseFloat(formData.valor || 0)) : productPrice;

                return (
                  <div key={productId} onClick={() => handleToggleProduct(productId)}
                    className={cn("p-4 rounded-lg border-2 cursor-pointer transition",
                      isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300')}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div>
                          <p className="font-semibold text-gray-800">{getProductName(product)}</p>
                          <p className="text-xs text-gray-500">{getProductCategory(product)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn("text-sm", isSelected ? 'line-through text-gray-500' : 'font-semibold text-gray-800')}>
                          S/ {productPrice.toFixed(2)}
                        </p>
                        {isSelected && formData.valor && (
                          <p className="text-lg font-bold text-blue-600">S/ {discountedPrice.toFixed(2)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={handleSave}
            disabled={loading || loadingProductos || !formData.nombre || !formData.valor || !formData.fecha_inicio || !formData.fecha_fin || formData.productoIds.length === 0}
            className={cn("flex-1 px-6 py-3 rounded-lg font-semibold", theme.classes.button.primary, theme.transitions.default,
              (loading || loadingProductos || !formData.nombre || !formData.valor || !formData.fecha_inicio || !formData.fecha_fin || formData.productoIds.length === 0) && "opacity-50 cursor-not-allowed")}>
            {loading ? 'Guardando...' : (editingDiscount ? 'Guardar Cambios' : 'Crear Descuento')}
          </button>
          <button onClick={() => setView('list')} disabled={loading || loadingProductos}
            className={cn("px-6 py-3 rounded-lg font-semibold", theme.classes.button.secondary, theme.transitions.default,
              (loading || loadingProductos) && "opacity-50 cursor-not-allowed")}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default DescuentoList;